import { m } from '@/locale/paraglide/messages';
import { ReleaseCard } from '@/components/changelog/release-card';
import Container from '@/components/layout/container';
import { DashboardHeader } from '@/components/layout/dashboard-header';
import { websiteConfig } from '@/config/website';
import { getChangelogReleases, type ChangelogRelease } from '@/lib/changelog';
import { formatDate } from '@/lib/formatter';
import { seo } from '@/lib/seo';
import {
  IconHistory,
  IconPhoto,
  IconSparkles,
  IconVideo,
} from '@tabler/icons-react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import type { ComponentType } from 'react';

type ChangelogFocusArea = {
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
};

const CHANGELOG_FOCUS_AREAS: ChangelogFocusArea[] = [
  {
    title: 'Prompt discovery',
    description: 'Gallery curation, search, filters, favorites, and reuse.',
    icon: IconSparkles,
  },
  {
    title: 'Image creation',
    description: 'Grok image editor, model options, quality, and credits.',
    icon: IconPhoto,
  },
  {
    title: 'Video workflows',
    description: 'Image-to-video, text-to-video, task history, and exports.',
    icon: IconVideo,
  },
];

export const Route = createFileRoute('/(pages)/changelog')({
  loader: () => {
    const releases = getChangelogReleases();
    if (!releases?.length) throw notFound();
    return releases;
  },
  head: () =>
    seo('/changelog', {
      title: `${m.changelog_title()} | ${websiteConfig.metadata?.name}`,
      description: m.changelog_description(),
    }),
  component: ChangelogPage,
});

function ChangelogPage() {
  const releases = Route.useLoaderData();
  const latestRelease = releases[0];
  if (!latestRelease) throw notFound();

  return (
    <div className="flex min-h-svh flex-col">
      <DashboardHeader showBack showSidebarTrigger={false} />
      <main className="border-t bg-background">
        <Container className="px-4 py-12 sm:py-16">
          <div className="mx-auto max-w-5xl space-y-10">
            <ChangelogHero latestRelease={latestRelease} />
            <ChangelogFocusAreas />
            <section className="space-y-6" aria-label={m.changelog_title()}>
              {releases.map((release) => (
                <ReleaseCard key={release.slug} release={release} />
              ))}
            </section>
          </div>
        </Container>
      </main>
    </div>
  );
}

function ChangelogHero({ latestRelease }: { latestRelease: ChangelogRelease }) {
  return (
    <section className="grid gap-8 border-b pb-10 lg:grid-cols-[1.45fr_0.75fr] lg:items-end">
      <div className="space-y-5">
        <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl">
          {m.changelog_title()}
        </h1>
        <p className="max-w-2xl text-lg text-muted-foreground leading-8">
          {m.changelog_subtitle()}
        </p>
      </div>
      <div className="rounded-lg border bg-muted/30 p-5">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <IconHistory className="size-4" />
          Latest release
        </div>
        <h2 className="mt-3 font-semibold text-xl tracking-tight">
          {latestRelease.version}
        </h2>
        <p className="mt-2 text-muted-foreground text-sm leading-6">
          {latestRelease.title}
        </p>
        <p className="mt-4 font-medium text-sm">
          {formatDate(new Date(latestRelease.date))}
        </p>
      </div>
    </section>
  );
}

function ChangelogFocusAreas() {
  return (
    <section className="grid gap-3 sm:grid-cols-3">
      {CHANGELOG_FOCUS_AREAS.map((area) => (
        <div
          key={area.title}
          className="rounded-lg border bg-card p-4 shadow-xs"
        >
          <area.icon className="size-5 text-primary" />
          <h2 className="mt-3 font-semibold text-base tracking-tight">
            {area.title}
          </h2>
          <p className="mt-2 text-muted-foreground text-sm leading-6">
            {area.description}
          </p>
        </div>
      ))}
    </section>
  );
}
